import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import APIInvoke from '../../../utils/APIInvoke';
import { DateTime } from 'luxon';
import { IconButton } from '@mui/material';
import ConfirmDialog from '../../../template/ConfirmDialog';
import LaunchIcon from '@mui/icons-material/Launch';
import { useNavigate } from 'react-router-dom';
import 'echarts-gl';

const Heatmap3d = ({ rawData = [] , isRunning, loading, project }) => {

    const [data, setData] = useState(undefined);
    const [timelapse, setTimelapse] = useState([]);
    const [showTimelapseDialog, setShowTimelapseDialog] = useState({
        show: false,
        startDate: null,
        endDate: null
    });
    const navigate = useNavigate();

    useEffect(() => {
        const heatmapData = rawData.map(item => {
            const { day, hour, minute, collection } = item._id;
            const date = DateTime.fromISO(`${day}T${hour.toString().padStart(2, '0')}:00:00`, { zone: 'UTC' }).setZone('local');
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const dayLabel = `${dayNames[date.weekday % 7]} ${date.day}`;
            const eventCount = Number(item.eventCount) || 0;
            return [date.hour, dayLabel, eventCount, collection, date];
        });
        setData(heatmapData);
    }, [rawData]);


    const formatNumber = (value) => {
        if (value < 1000) return value.toString();
        if (value < 1000000) return `${(value / 1000).toFixed(1)}k`;
        if (value < 1000000000) return `${(value / 1000000).toFixed(1)}M`;
        return `${(value / 1000000000).toFixed(1)}B`;
    };

    const getOption = () => {
        const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}`);
        const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - i);
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            return `${dayNames[date.getDay()]} ${date.getDate()}`;
        }).reverse();

        // Adaptar los datos para bar3D: [x, y, z]
        // x: hora, y: día, z: valor (eventCount)
        let max = 0;
        const bar3DData = (data || []).map(item => {
            // item: [hour, dayLabel, eventCount, collection, date]
            if (item[2] > max) max = item[2];
            return {
                value: [hours.indexOf(item[0].toString().padStart(2, '0')), days.indexOf(item[1]), item[2]],
                collection: item[3],
                date: item[4]
            };
        });

        return {
            tooltip: {
                formatter: (params) => {
                    const { value, collection, date } = params.data;
                    const [x, y, z] = value;
                    return `Events: ${formatNumber(z)}<br>${days[y]}-${hours[x]}HRS`;
                }
            },
            visualMap: {
                max: max,
                inRange: {
                    color: [
                        '#313695',
                        '#4575b4',
                        '#74add1',
                        '#abd9e9',
                        '#e0f3f8',
                        '#ffffbf',
                        '#fee090',
                        '#fdae61',
                        '#f46d43',
                        '#d73027',
                        '#a50026'
                    ]
                }
            },
            xAxis3D: {
                type: 'category',
                name: 'Hour',
                data: hours,
                axisLabel: { fontSize: 10 }
            },
            yAxis3D: {
                type: 'category',
                name: 'Day',
                data: days,
                axisLabel: { fontSize: 10 }
            },
            zAxis3D: {
                type: 'value',
                name: 'Events',
                axisLabel: { fontSize: 10 }
            },
            grid3D: {
                boxWidth: 120,
                boxDepth: 60,
                viewControl: {
                    // autoRotate: true,
                },
                light: {
                    main: {
                        intensity: 1.2,
                        shadow: true
                    },
                    ambient: {
                        intensity: 0.3
                    }
                }
            },
            series: [{
                type: 'bar3D',
                data: bar3DData,
                shading: 'lambert',
                label: {
                    show: false,
                    fontSize: 10,
                    borderWidth: 1
                },
                itemStyle: {
                    //opacity: 0.8
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 12,
                        color: '#fff',
                        backgroundColor: 'rgba(0,0,0,0.7)'
                    },
                    itemStyle: {
                        color: '#ff9933'
                    }
                }
            }]
        };
    };

    const handleChartClick = async (params) => {
        const { value, collection, date } = params.data;
        const startDate = DateTime.fromMillis(date.ts);
        const endDate = startDate.plus({ hours: 1 });
        const response = await (await APIInvoke.invokeGET(`/projects/${project}/dashboard/events/detail/hourly?start=${startDate.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss")}&end=${endDate.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss")}`)).json();
        setTimelapse(response.body)
        setShowTimelapseDialog({
            show: true,
            startDate: startDate,
            endDate: endDate
        })
    }

    const handleClickTimelapse = (lapse) => {
        const startDate = showTimelapseDialog.startDate
        const endDate = showTimelapseDialog.endDate;
        const collection = lapse._id.collection;

        const queryParam = {
            "query": {
                "$and": [
                    {
                        "lastReceivedAt": {
                            "$gte": `ISODate("${startDate.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSSZZZ")}")`
                        }
                    },
                    {
                        "lastReceivedAt": {
                            "$lte": `ISODate("${endDate.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSSZZZ")}")`
                        }
                    }
                ]
            },
            "queryBuilder": {
                "combinator": "and",
                "rules": [
                    {
                        "field": "lastReceivedAt",
                        "operator": ">=",
                        "value": startDate.toFormat("yyyy-MM-dd'T'HH:mm"),
                        "id": "5e00e8bd-c99e-44ac-bb89-3ea3589b23d1"
                    },
                    {
                        "field": "lastReceivedAt",
                        "operator": "<=",
                        "valueSource": "value",
                        "value": endDate.toFormat("yyyy-MM-dd'T'HH:mm"),
                        "id": "1ca020bb-67b5-413a-8148-eae117c9b201"
                    }
                ],
                "id": "4cf14825-d545-47a2-b83f-bb7653e2dabf"
            }
        }

        const queryParam64 = btoa(JSON.stringify(queryParam));

        navigate(`/projects/${project}/collections/${collection}?query=${queryParam64}`);
    };

    return (
        <div >
            <ConfirmDialog color="green" title="Timelapse detail" open={showTimelapseDialog.show} confirmMessage="OK" onConfirm={() => setShowTimelapseDialog({ ...showTimelapseDialog, show: false })} onClose={() => setShowTimelapseDialog({ ...showTimelapseDialog, show: false })}>
                {timelapse.length === 0 && <p>No events found</p>}
                {timelapse.map(lapse => (
                    <div className="flex justify-between p-2 items-center gap-8 min-w-80 hover:bg-gray-100">
                        <p className="text-sm">{lapse._id.collection}: {lapse.eventCount}</p>
                        <IconButton onClick={() => handleClickTimelapse(lapse)} color="primary" size="small" ><LaunchIcon fontSize="small" /></IconButton>
                    </div>
                ))}
            </ConfirmDialog>
            <div className="h-[600px]">
                <ReactECharts
                    option={getOption()}
                    showLoading={loading}
                    onEvents={{
                        click: handleChartClick
                    }}
                    style={{ height: '100%' }}
                />
            </div>
        </div >

    );
}
export default Heatmap3d;